<template>
  <v-col cols="12" v-if="['single'].includes(giftType)">
    <span class="campaign-card__text">
      Single choice:
    </span>
    <span class="campaign-card__subtext" v-if="allProductsFromInventory">
      Inventory items
    </span>

    <template v-else>
      <span class="campaign-card__subtext">
        {{ minPrice | priceFilter }}
      </span>

      <span
        class="campaign-card__subtext"
        v-if="maxPrice"
      >
        -
        {{ maxPrice | priceFilter }}
      </span>
    </template>
  </v-col>

  <v-col cols="12" v-else-if="['multiple'].includes(giftType)">
    <span class="campaign-card__text">
      Multiple choice ({{ maxSelection }} {{ maxSelection > 1 ? 'Items' : 'Item' }}):
    </span>

    <span class="campaign-card__subtext" v-if="allProductsFromInventory">
      Inventory items
    </span>

    <span class="campaign-card__subtext" v-else>
      {{ minPrice | priceFilter }}
      -
      {{ maxPrice | priceFilter }}
    </span>
  </v-col>

  <v-col cols="12" v-else>
    <span class="campaign-card__text">
      GiftCoin:
    </span>

    <span class="campaign-card__subtext">
      {{ coinValue | priceFilter({
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0
      }) }} value
    </span>
  </v-col>
</template>

<script>
import { isProductFromInventory } from '@/utils/isProductFromInventory'

const sumOf = (prices) => prices.reduce((acc, price) => acc + price, 0)

export default {
  name: 'TheCampaignCardType',
  props: {
    giftType: {
      type: String,
      required: false,
      default: 'single'
    },
    priceRange: {
      type: Object,
      required: true
    },
    giftSettings: {
      type: Object,
      required: false,
      default: null
    },
    giftProducts: {
      type: Array,
      required: false,
      default: () => ([])
    },
    isPrepaid: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  computed: {
    maxSelection () {
      return this.giftSettings?.max_allowed_selection || 1
    },
    coinValue () {
      return this.giftSettings?.coin_value || 0
    },
    productsPrices () {
      return this.giftProducts.map(product => Number(product.product.price) || 0)
    },
    // Backend sends price 0 for every inventory item, also the ones that are
    // out of stock (inventory.qty 0), so zero prices are skipped as well.
    nonInventoryPrices () {
      const { giftProducts, isPrepaid } = this
      return giftProducts
        .filter(product => !isProductFromInventory(product, isPrepaid))
        .map(product => Number(product.product.price) || 0)
        .filter(price => price > 0)
    },
    hasInventoryProducts () {
      return this.nonInventoryPrices.length < this.giftProducts.length
    },
    allProductsFromInventory () {
      return this.giftProducts.length > 0 && this.nonInventoryPrices.length === 0
    },
    // Inventory items cost nothing to send, so in a mixed campaign the lowest
    // price comes from the gift options that are not from inventory.
    minPrice () {
      const { giftType, priceRange, nonInventoryPrices, hasInventoryProducts, maxSelection } = this
      if (giftType === 'multiple') {
        return sumOf([...nonInventoryPrices].sort((a, b) => a - b).slice(0, maxSelection))
      }
      return hasInventoryProducts ? Math.min(...nonInventoryPrices) : priceRange.min
    },
    maxPrice () {
      const { giftType, priceRange, productsPrices, maxSelection } = this
      if (giftType === 'multiple') {
        return sumOf([...productsPrices].sort((a, b) => b - a).slice(0, maxSelection))
      }
      return priceRange.max
    }
  }
}
</script>

<style lang="scss" scoped>
.campaign-card {
  background: #f6f6f6;
  &__text, &__subtext {
    font-family: 'Lato-Regular', sans-serif;
    font-size: 14px;
    line-height: 17px;
    letter-spacing: 0.19px;
    color: #222325;
  }

  &__text {
    font-family: 'Lato-Bold', sans-serif;
  }
}
</style>
